import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtMoney } from "../lib/api";
import { PageHeader, DataTable, StatusBadge, DateRangeFilter, filterByDate } from "../components/Shell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Plus, PencilSimpleLine, ReceiptX } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import DeleteButton from "../components/DeleteButton";

const EMPTY = {
  nom: "",
  prenom: "",
  nom_scene: "",
  telephone: "",
  email: "",
  mot_de_passe: "",
  siret: "",
  raison_sociale_siret: "",
  titulaire_rib: "",
  iban: "",
  banque: "",
  en_guso: false,
  numero_guso: "",
  billet_avion_rembourser: false,
  montant_billet_avion: 0,
  note_complementaire: "",
  notes: "",
};

export default function Dancers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [dancers, setDancers] = useState([]);
  const [pendingByDancer, setPendingByDancer] = useState({});
  const [filter, setFilter] = useState("active");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [range, setRange] = useState({ start: "", end: "" });
  const [searchParams, setSearchParams] = useSearchParams();
  const urlFilter = searchParams.get("filter") || "";
  const nav = useNavigate();

  const load = async () => {
    const [dd, reqs] = await Promise.all([
      api.get("/dancers", { params: filter === "all" ? {} : { statut: filter } }),
      api.get("/payment-requests"),
    ]);
    setDancers(dd.data);
    const pending = {};
    reqs.data.forEach((r) => {
      if (["demande_envoyee", "a_payer"].includes(r.statut)) {
        pending[r.danseuse_id] = (pending[r.danseuse_id] || 0) + 1;
      }
    });
    setPendingByDancer(pending);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filter]);

  const submit = async () => {
    if (!form.nom || !form.prenom || !form.nom_scene || !form.telephone) {
      toast.error("Nom, prénom, nom de scène et téléphone requis");
      return;
    }
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.mot_de_passe) delete payload.mot_de_passe;
      await api.post("/dancers", payload);
      toast.success("Danseuse créée");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div>
      <PageHeader
        testId="dancers-header"
        title="Danseuses"
        subtitle="Gérer les fiches, bookings, comptes et règlements."
        actions={
          <div className="flex gap-3 items-center flex-wrap">
            <DateRangeFilter testId="dancers-filter" start={range.start} end={range.end} onChange={setRange} />
            <Button
              data-testid="new-dancer-btn"
              onClick={() => setOpen(true)}
              className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black"
            >
              <Plus size={16} className="mr-2" /> Nouvelle danseuse
            </Button>
          </div>
        }
      />

      <Tabs value={filter} onValueChange={setFilter} className="mb-6">
        <TabsList className="bg-[#121217] border border-[#27272a]">
          <TabsTrigger value="active" data-testid="tab-active">Actives</TabsTrigger>
          <TabsTrigger value="archivee" data-testid="tab-archivees">Archivées</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">Toutes</TabsTrigger>
        </TabsList>
      </Tabs>

      {urlFilter && (
        <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-md border" style={{ borderColor: "#fbbf2450", background: "#fbbf2410" }}>
          <span className="text-xs text-[#fbbf24]">
            Filtre actif : <b>{urlFilter === "sans_siret_rib" ? "Sans SIRET ou RIB" : "Booking à clôturer"}</b>
          </span>
          <button
            data-testid="clear-url-filter-dancers"
            onClick={() => setSearchParams({})}
            className="text-xs text-[#a1a1aa] hover:text-white underline ml-2"
          >
            effacer
          </button>
        </div>
      )}

      <DataTable
        columns={[
          { title: "Scène", render: (r) => <b className="text-white">{r.nom_scene}</b> },
          { title: "Nom réel", render: (r) => `${r.prenom} ${r.nom}` },
          { title: "Téléphone", key: "telephone" },
          {
            title: "Booking",
            render: (r) => (r.booking_actif ? <StatusBadge status="actif" /> : <span className="text-[#71717a]">-</span>),
          },
          {
            title: "Solde",
            align: "right",
            render: (r) => <b style={{ color: r.solde_disponible > 0 ? "#34d399" : "#a1a1aa" }}>{fmtMoney(r.solde_disponible)}</b>,
          },
          {
            title: "Demande",
            render: (r) =>
              pendingByDancer[r.id] ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border" style={{ color: "#fbbf24", borderColor: "#fbbf2450", background: "#fbbf2414" }}>
                  <ReceiptX size={12} /> {pendingByDancer[r.id]} en attente
                </span>
              ) : <span className="text-[#71717a]">—</span>,
          },
          { title: "Statut", render: (r) => <StatusBadge status={r.statut} /> },
          {
            title: "Actions",
            render: (r) => (
              <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                {r.statut === "active" && r.booking_actif && (
                  <Button
                    data-testid={`quick-account-${r.id}`}
                    size="sm"
                    onClick={() => nav(`/accounts?dancer=${r.id}`)}
                    className="h-8 bg-[#fbbf24] hover:bg-[#f59e0b] text-black"
                  >
                    <PencilSimpleLine size={14} className="mr-1" /> Saisir
                  </Button>
                )}
                {isAdmin && (
                  <DeleteButton
                    url={`/dancers/${r.id}`}
                    label={`la danseuse ${r.nom_scene}`}
                    hint="Cela supprimera aussi ses bookings, comptes journaliers, demandes et règlements."
                    onDone={load}
                    testId={`delete-dancer-${r.id}`}
                  />
                )}
              </div>
            ),
          },
        ]}
        rows={(() => {
          let rows = (range.start || range.end) ? filterByDate(dancers, "date_creation", range.start, range.end) : dancers;
          if (urlFilter === "sans_siret_rib") {
            rows = rows.filter((d) => !d.siret || !d.titulaire_rib || !d.iban);
          } else if (urlFilter === "booking_a_cloturer") {
            const today = new Date().toISOString().slice(0, 10);
            // keep dancers whose active booking has date_fin_prevue < today; we don't have direct bookings here, so rely on booking_actif flag and let manageuse click through
            rows = rows.filter((d) => d.booking_actif);
          }
          return rows;
        })()}
        rowKey="id"
        onRowClick={(r) => nav(`/dancers/${r.id}`)}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121217] border-[#27272a] text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Nouvelle danseuse</DialogTitle>
          </DialogHeader>
          <DancerForm form={form} setForm={setForm} isNew />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="border-[#27272a] text-white hover:bg-[#1a1a21]"
            >
              Annuler
            </Button>
            <Button data-testid="submit-dancer" onClick={submit} className="bg-[#fbbf24] hover:bg-[#f59e0b] text-black">
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Inner form components — defined OUTSIDE DancerForm to keep their identity
// stable across renders. Defining them inside the parent re-creates the
// component on every keystroke, causing React to unmount/remount the <input>
// and lose focus after each character typed.
function FormField({ k, label, type = "text", placeholder, cols = 1, form, setForm, disabledFields }) {
  return (
    <div className={cols === 2 ? "col-span-2" : ""}>
      <Label className="label-xs mb-1 block">{label}</Label>
      <Input
        data-testid={`field-${k}`}
        type={type}
        value={form[k] ?? ""}
        onChange={(e) =>
          setForm({ ...form, [k]: type === "number" ? Number(e.target.value) : e.target.value })
        }
        placeholder={placeholder}
        disabled={disabledFields.includes(k)}
        className="bg-[#09090b] border-[#27272a] text-white focus:ring-[#fbbf24]/50 h-10"
      />
    </div>
  );
}

function FormSection({ title, num, children }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
          style={{ background: "#fbbf24", color: "#09090b" }}
        >
          {num}
        </div>
        <div className="label-xs" style={{ color: "#fbbf24" }}>{title}</div>
      </div>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function FormToggle({ k, label, form, setForm }) {
  return (
    <div className="col-span-2 flex items-center justify-between rounded-md border border-[#27272a] bg-[#09090b] p-3">
      <span className="text-sm text-white">{label}</span>
      <div className="flex gap-2">
        {[true, false].map((v) => (
          <button
            key={String(v)}
            type="button"
            data-testid={`toggle-${k}-${v ? "yes" : "no"}`}
            onClick={() => setForm({ ...form, [k]: v })}
            className="h-9 px-4 rounded-md border text-sm font-medium transition-all"
            style={{
              color: form[k] === v ? "#09090b" : "#a1a1aa",
              background: form[k] === v ? "#fbbf24" : "transparent",
              borderColor: form[k] === v ? "transparent" : "#27272a",
            }}
          >
            {v ? "Oui" : "Non"}
          </button>
        ))}
      </div>
    </div>
  );
}

export function DancerForm({ form, setForm, isNew = false, disabledFields = [] }) {
  // Shorthand so the JSX below stays readable — these are NOT new component
  // definitions, they're just prop-bound renderers calling the stable
  // FormField/FormToggle components defined above.
  const field = (props) => (
    <FormField key={props.k} {...props} form={form} setForm={setForm} disabledFields={disabledFields} />
  );
  const toggle = (props) => (
    <FormToggle key={props.k} {...props} form={form} setForm={setForm} />
  );

  return (
    <div className="space-y-2">
      <FormSection title="Identité" num="1">
        {field({ k: "prenom", label: "Prénom (civil) *" })}
        {field({ k: "nom", label: "Nom (civil) *" })}
        {field({ k: "nom_scene", label: "Nom de danseuse *", cols: 2 })}
        {field({ k: "telephone", label: "Téléphone *", type: "tel", placeholder: "+596..." })}
        {field({ k: "email", label: "Email", type: "email" })}
        {isNew && field({ k: "mot_de_passe", label: "Mot de passe (défaut : Tantra2026)", cols: 2 })}
      </FormSection>

      <FormSection title="Administratif" num="2">
        {field({ k: "siret", label: "SIRET", placeholder: "14 chiffres" })}
        {field({ k: "raison_sociale_siret", label: "Raison sociale" })}
        {toggle({ k: "en_guso", label: "En GUSO ?" })}
        {form.en_guso && field({ k: "numero_guso", label: "Numéro GUSO", cols: 2 })}
      </FormSection>

      <FormSection title="Paiement" num="3">
        {field({ k: "titulaire_rib", label: "Titulaire du compte" })}
        {field({ k: "banque", label: "Banque" })}
        {field({ k: "iban", label: "IBAN", cols: 2, placeholder: "FR76..." })}
      </FormSection>

      <FormSection title="Déplacements / remboursement" num="4">
        {toggle({ k: "billet_avion_rembourser", label: "Billet d'avion à rembourser ?" })}
        {form.billet_avion_rembourser && (
          <>
            {field({ k: "montant_billet_avion", label: "Montant à rembourser (€)", type: "number" })}
            <div className="col-span-2">
              <Label className="label-xs mb-1 block">Note complémentaire</Label>
              <Textarea
                data-testid="field-note_complementaire"
                value={form.note_complementaire || ""}
                onChange={(e) => setForm({ ...form, note_complementaire: e.target.value })}
                placeholder="Vol, compagnie, référence billet..."
                className="bg-[#09090b] border-[#27272a] text-white"
              />
            </div>
          </>
        )}
      </FormSection>

      <div className="mb-2">
        <Label className="label-xs mb-1 block">Notes internes (back-office)</Label>
        <Textarea
          data-testid="field-notes"
          value={form.notes || ""}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="bg-[#09090b] border-[#27272a] text-white"
        />
      </div>
    </div>
  );
}
